import { ElephantLogomark } from "$components/elephant-logomark";
import { ErrorBoundary } from "$components/error-boundary";
import { ErrorState } from "$components/error-state";
import { LoadingPage } from "$components/loading/loading-page";
import { ConfigProvider } from "$global-providers/config-provider/provider";
import { GlobalProviders } from "$global-providers/global-providers";
import { DevToolsOverlay } from "$routes/_lib/devtools-overlay/dev-tools-overlay";
import { Auth0CallbackPage } from "$routes/auth0/callback/page";
import { DevPatientRoutes } from "$routes/dev-patient/routes";
import { AppLayout } from "$routes/layout";
import { RedirectToAuth0Login } from "$routes/login/page";
import { NotFoundPage as NotFound } from "$routes/not-found-page";
import { HomePage } from "$routes/page";
import { SignOut } from "$routes/sign-out/page";
import { UnauthorizedPage } from "$routes/unauthorized/page";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { Role } from "elephant-web/gql";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import GlobalAppIssueOverlay from "./global-providers/global-app-issue-overlay/global-app-issue-overlay";
import { Freshdesk } from "./routes/_lib/freshdesk";
import { Overlays } from "./routes/_lib/overlays/overlays";
import { ClinicalProductManagementRoutes } from "./routes/clinical-product-management/routes";
import "$routes/_lib/validation/configure-yup";
import { LocationsProvider } from "$routes/_lib/locations-context";
import { InventoryRoutes } from "$routes/inventory/routes";
import { PatientRoutes } from "$routes/patient/[:patientId]/routes";
import { OrganisationManagementRoutes } from "./routes/organisation-management/routes";
import "$routes/_lib/polyfills/core-js";
import { lazyLoad } from "$routes/_lib/lazy-load";
import useLoadPolyfillsEffect from "$routes/_lib/polyfills/use-load-polyfills-effect";
import { RequireAuth } from "$routes/_lib/require-auth";
import { OfflineRoute } from "$routes/offline/routes";
import { PriceListRoutes } from "$routes/price-list/routes";
import { AdminRoutes } from "./routes/admin/routes";
import { ClinicAdminRoutes } from "./routes/clinic-admin/routes";
import { EpocusRoutes } from "./routes/epocus/routes";
import { NewPatientRoutes } from "./routes/new/routes";
import { PatientSearchRoutes } from "./routes/patient-search/routes";
import { RegistersRoutes } from "./routes/registers/routes";
import { SkuManagementRoutes } from "./routes/sku-management/routes";
import { FirstSignInPage } from "./routes/user/first-sign-in/page";

const EditPatientRoutes = lazyLoad(() =>
  import(
    /* webpackChunkName: "EditPatientPage" */ "$routes/edit/patient/routes"
  ).then((module) => ({ default: module.EditPatientRoutes })),
);
const ReissueCardPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "ReissueCardPage" */ "$routes/card/reissue/page"
  ).then((module) => ({ default: module.ReissueCardPage })),
);
const WardOverviewPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "WardOverviewPage" */ "$routes/ward-patient-list/page"
  ).then((module) => ({ default: module.WardOverviewPage })),
);
const WardSuppliesPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "WardSuppliesPage" */ "$routes/ward-supplies/page"
  ).then((module) => ({ default: module.WardSuppliesPage })),
);
const QueueOverviewPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "QueueManagementPage" */ "$routes/patient-queue-management/page"
  ).then((module) => ({ default: module.QueueOverviewPage })),
);
const PatientQueuePage = lazyLoad(
  () =>
    import(/* webpackChunkName: "PatientQueuePage" */ "$routes/queue/chunk"),
);
const ScanPatientCardPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "ScanPatientCardPage" */ /* webpackPrefetch: true */ "$routes/scan-card/page"
  ).then((module) => ({ default: module.ScanPatientCardPage })),
);
const UserSettingsPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "UserSettingsPage" */ /* webpackPrefetch: true */ "./routes/user/settings/page"
    ),
);
const DisabledPatientPortalV1Page = lazyLoad(() =>
  import(
    /* webpackChunkName: "DisabledPatientPortalV1Page" */ "$routes/signin/patient/page"
  ).then((module) => ({ default: module.Page })),
);
const ContextFreeErrorPage: FC<{
  details?: { name: string; message: string; stack?: string };
}> = ({ details }) => (
  // Plain-string translations keep this renderable above the intl/MUI providers.
  <ErrorState
    variant="boundary"
    details={details}
    title="Something went wrong"
    retryLabel="Try again"
    contactSupport="If this persists, please report this to the Elephant team with a photo of the below:"
  />
);

const PrivateProviders: FCwC = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (!isLoading && !isAuthenticated) {
    // If we do not have a session token at this point,
    // the user probably tried to go directly to a private route.
    // Clear any user state and redirct to the login page
    return <SignOut />;
  }

  return (
    <LocationsProvider>
      <DevToolsOverlay />
      <Freshdesk />
      <Overlays />
      {children}
      <GlobalAppIssueOverlay />
    </LocationsProvider>
  );
};

const App: FC = () => {
  const polyfillsLoaded = useLoadPolyfillsEffect();

  React.useEffect(() => {
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  if (!polyfillsLoaded) {
    return null;
  }

  return (
    <ErrorBoundary
      name="root"
      // We must override the default ErrorPage so we don't use any contexts we don't have access to.
      fallback={(error) => (
        <ContextFreeErrorPage
          details={{
            name: error.error.name,
            message: error.error.message,
            stack: error.componentStack,
          }}
        />
      )}
    >
      <ConfigProvider
        configServicePath={CONFIG_SERVICE_PATH}
        errorElement={(error) => (
          <ContextFreeErrorPage
            details={{
              name: error.name,
              message: error.message,
              stack: error.stack,
            }}
          />
        )}
        loadingElement={
          <LoadingPage
            // We must override the logo and translations so we don't use any contexts we don't have access to.
            logomark={<ElephantLogomark />}
            translations={{ loadingTip: "Loading..." }}
          />
        }
      >
        <RouterProvider
          router={Sentry.wrapCreateBrowserRouter(createBrowserRouter)(
            createRoutesFromElements(
              <Route element={<GlobalProviders />}>
                <Route index element={<HomePage />} />
                <Route path="login" element={<RedirectToAuth0Login />} />
                <Route path="offline/*" element={<OfflineRoute />} />
                <Route path="sign-out" element={<SignOut />} />
                <Route path="signin">
                  <Route
                    path="patient/*"
                    element={<DisabledPatientPortalV1Page />}
                  />
                </Route>
                <Route path="auth0">
                  <Route path="callback" element={<Auth0CallbackPage />} />
                </Route>
                <Route path="unauthorized" element={<UnauthorizedPage />} />
                <Route
                  path="epocus/*"
                  element={
                    <RequireAuth>
                      <PrivateProviders>
                        <EpocusRoutes />
                      </PrivateProviders>
                    </RequireAuth>
                  }
                />
                <Route
                  path="*"
                  element={
                    <RequireAuth>
                      <PrivateProviders>
                        <AppLayout />
                      </PrivateProviders>
                    </RequireAuth>
                  }
                >
                  <Route path="scan-card" element={<ScanPatientCardPage />} />
                  <Route
                    path="ward-patient-list"
                    element={<WardOverviewPage />}
                  />
                  <Route path="ward-supplies" element={<WardSuppliesPage />} />
                  <Route
                    path="patient-queue-management"
                    element={<QueueOverviewPage />}
                  />
                  <Route path="queue" element={<PatientQueuePage />} />
                  <Route path="user">
                    <Route path="settings" element={<UserSettingsPage />} />
                    <Route path="first-sign-in" element={<FirstSignInPage />} />
                  </Route>
                  <Route
                    path="edit/patient/*"
                    element={
                      <RequireAuth except={[Role.Root]}>
                        <EditPatientRoutes />
                      </RequireAuth>
                    }
                  />
                  <Route path="admin/*" element={<AdminRoutes />} />
                  <Route path="patient/*" element={<PatientRoutes />} />
                  <Route path="dev-patient/*" element={<DevPatientRoutes />} />
                  <Route
                    path="clinic-admin/*"
                    element={
                      <RequireAuth only={[Role.Clinic]}>
                        <ClinicAdminRoutes />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="organisation-management/*"
                    element={
                      <RequireAuth
                        only={[
                          Role.Admin,
                          Role.Clinic,
                          Role.Elephant,
                          Role.Root,
                        ]}
                      >
                        <OrganisationManagementRoutes />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="patient-search/*"
                    element={<PatientSearchRoutes />}
                  />
                  <Route
                    path="clinical-product-management/*"
                    element={
                      <RequireAuth only={[Role.Elephant]}>
                        <ClinicalProductManagementRoutes />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="sku-management/*"
                    element={
                      <RequireAuth only={[Role.Elephant]}>
                        <SkuManagementRoutes />
                      </RequireAuth>
                    }
                  />
                  <Route path="new/*" element={<NewPatientRoutes />} />
                  <Route
                    path="card/reissue"
                    element={
                      <RequireAuth
                        except={[Role.Admin, Role.Clinic, Role.Root]}
                      >
                        <ReissueCardPage />
                      </RequireAuth>
                    }
                  />
                  <Route path="price-list/*" element={<PriceListRoutes />} />
                  <Route
                    path="registers/*"
                    element={
                      <RequireAuth>
                        <RegistersRoutes />
                      </RequireAuth>
                    }
                  />
                  <Route path="inventory/*" element={<InventoryRoutes />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>,
            ),
          )}
        />
      </ConfigProvider>
    </ErrorBoundary>
  );
};

ReactDOM.createRoot(document.getElementById("app")).render(<App />);
